import React, { useEffect, useState, useMemo } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    useTheme,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Pagination,
    Stack,
    TextField,
    InputAdornment,
    IconButton,
    Tooltip,
    Checkbox,
} from '@mui/material';
import { Search, Edit2, Trash2, X, AlertCircle, CheckCircle } from 'lucide-react';
import { Layout } from '@layout/Layout';
import { useAppTheme } from '@contexts/ThemeContext';
import { getAllUsers, User, PaginationInfo, updateUser, deleteUser, UpdateUserData, getAssessmentUsers, updateUserLeaderFlag, AssessmentUser } from '@services/AssessmentService';
import Spinner from './components/Spinner';
import './Users.css';

const Users: React.FC = () => {
    const theme = useTheme();
    const { isDarkMode } = useAppTheme();
    const [allUsers, setAllUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchQuery, setSearchQuery] = useState('');
    const usersPerPage = 20;

    // Edit modal state
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState<User | null>(null);
    const [editFormData, setEditFormData] = useState<UpdateUserData>({ name: '', email: '', dob: '' });
    const [editLoading, setEditLoading] = useState(false);

    // Delete modal state
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [deletingUser, setDeletingUser] = useState<User | null>(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    // Success/Error messages
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        const fetchUsers = async () => {
            setLoading(true);
            try {
                // Fetch assessment users with leader flag
                const response = await getAssessmentUsers();
                if (response.success && response.data) {
                    // Map AssessmentUser to User format for compatibility
                    const mappedUsers: User[] = response.data.map((user: AssessmentUser) => ({
                        userId: user.id,
                        name: user.name,
                        email: user.email,
                        dob: '',
                        rollId: 0,
                        role: 'User',
                        createdAt: '',
                        is_leader: user.is_leader
                    }));
                    setAllUsers(mappedUsers);
                    setError(null);
                } else {
                    setError(response.error?.message || 'Failed to fetch users');
                }
            } catch (err) {
                setError('An unexpected error occurred');
                console.error('Error fetching users:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchUsers();
    }, []);

    // Filter users based on search query
    const filteredUsers = useMemo(() => {
        if (!searchQuery.trim()) {
            return allUsers;
        }

        return allUsers.filter(user =>
            user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
            user.name.toLowerCase().includes(searchQuery.toLowerCase())
        );
    }, [allUsers, searchQuery]);

    // Paginate filtered users
    const paginatedUsers = useMemo(() => {
        const startIndex = (currentPage - 1) * usersPerPage;
        const endIndex = startIndex + usersPerPage;
        return filteredUsers.slice(startIndex, endIndex);
    }, [filteredUsers, currentPage, usersPerPage]);

    // Calculate total pages
    const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

    // Reset to page 1 when search query changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery]);

    const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
        setCurrentPage(value);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const formatDate = (dateString: string) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const formatDateForInput = (dateString: string) => {
        const date = new Date(dateString);
        return date.toISOString().split('T')[0];
    };

    // Open edit modal
    const handleEditClick = (user: User) => {
        setEditingUser(user);
        setEditFormData({
            name: user.name,
            email: user.email,
            dob: formatDateForInput(user.dob)
        });
        setEditModalOpen(true);
    };

    // Close edit modal
    const handleEditClose = () => {
        setEditModalOpen(false);
        setEditingUser(null);
        setEditFormData({ name: '', email: '', dob: '' });
    };

    // Handle edit form submission
    const handleEditSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!editingUser) return;

        setEditLoading(true);
        setErrorMessage(null);
        setSuccessMessage(null);

        // Track start time to ensure minimum 1 second loading
        const startTime = Date.now();

        try {
            const response = await updateUser(editingUser.userId, editFormData);

            // Calculate remaining time to meet minimum 1 second
            const elapsedTime = Date.now() - startTime;
            const remainingTime = Math.max(0, 1000 - elapsedTime);

            // Wait for remaining time to ensure minimum 1 second loading
            await new Promise(resolve => setTimeout(resolve, remainingTime));

            if (response.success) {
                setEditLoading(false);
                setSuccessMessage('User updated successfully');
                setEditModalOpen(false);
                setEditingUser(null);
                setEditFormData({ name: '', email: '', dob: '' });

                // Refresh users list
                const usersResponse = await getAssessmentUsers();
                if (usersResponse.success && usersResponse.data) {
                    const mappedUsers: User[] = usersResponse.data.map((user: AssessmentUser) => ({
                        userId: user.id,
                        name: user.name,
                        email: user.email,
                        dob: '',
                        rollId: 0,
                        role: 'User',
                        createdAt: '',
                        is_leader: user.is_leader
                    }));
                    setAllUsers(mappedUsers);
                }

                // Auto-hide success message after 1 second
                setTimeout(() => setSuccessMessage(null), 1000);
            } else {
                const elapsedTime2 = Date.now() - startTime;
                const remainingTime2 = Math.max(0, 1000 - elapsedTime2);
                await new Promise(resolve => setTimeout(resolve, remainingTime2));

                setEditLoading(false);
                setErrorMessage(response.error?.message || 'Failed to update user');
                setTimeout(() => setErrorMessage(null), 1000);
            }
        } catch (err: any) {
            const elapsedTime = Date.now() - startTime;
            const remainingTime = Math.max(0, 1000 - elapsedTime);
            await new Promise(resolve => setTimeout(resolve, remainingTime));

            setEditLoading(false);
            setErrorMessage('An unexpected error occurred');
            setTimeout(() => setErrorMessage(null), 1000);
        }
    };

    // Open delete modal
    const handleDeleteClick = (user: User) => {
        setDeletingUser(user);
        setDeleteModalOpen(true);
    };

    // Close delete modal
    const handleDeleteClose = () => {
        setDeleteModalOpen(false);
        setDeletingUser(null);
    };

    // Handle delete confirmation
    const handleDeleteConfirm = async () => {
        if (!deletingUser) return;

        setDeleteLoading(true);
        setErrorMessage(null);
        setSuccessMessage(null);

        // Track start time to ensure minimum 1 second loading
        const startTime = Date.now();

        try {
            const response = await deleteUser(deletingUser.userId);

            // Calculate remaining time to meet minimum 1 second
            const elapsedTime = Date.now() - startTime;
            const remainingTime = Math.max(0, 1000 - elapsedTime);

            // Wait for remaining time to ensure minimum 1 second loading
            await new Promise(resolve => setTimeout(resolve, remainingTime));

            if (response.success) {
                setDeleteLoading(false);
                setSuccessMessage('User deleted successfully');
                setDeleteModalOpen(false);
                setDeletingUser(null);

                // Refresh users list
                const usersResponse = await getAssessmentUsers();
                if (usersResponse.success && usersResponse.data) {
                    const mappedUsers: User[] = usersResponse.data.map((user: AssessmentUser) => ({
                        userId: user.id,
                        name: user.name,
                        email: user.email,
                        dob: '',
                        rollId: 0,
                        role: 'User',
                        createdAt: '',
                        is_leader: user.is_leader
                    }));
                    setAllUsers(mappedUsers);
                }

                // Auto-hide success message after 1 second
                setTimeout(() => setSuccessMessage(null), 1000);
            } else {
                const elapsedTime2 = Date.now() - startTime;
                const remainingTime2 = Math.max(0, 1000 - elapsedTime2);
                await new Promise(resolve => setTimeout(resolve, remainingTime2));

                setDeleteLoading(false);
                setErrorMessage(response.error?.message || 'Failed to delete user');
                setTimeout(() => setErrorMessage(null), 1000);
            }
        } catch (err: any) {
            const elapsedTime = Date.now() - startTime;
            const remainingTime = Math.max(0, 1000 - elapsedTime);
            await new Promise(resolve => setTimeout(resolve, remainingTime));

            setDeleteLoading(false);
            setErrorMessage('An unexpected error occurred');
            setTimeout(() => setErrorMessage(null), 1000);
        }
    };

    // Handle leader flag toggle
    const handleLeaderToggle = async (user: User) => {
        const newLeaderValue = user.is_leader === 1 ? 0 : 1;

        try {
            const response = await updateUserLeaderFlag(user.userId, newLeaderValue);

            if (response.success) {
                // Update the user in local state
                setAllUsers(prevUsers =>
                    prevUsers.map(u =>
                        u.userId === user.userId
                            ? { ...u, is_leader: newLeaderValue }
                            : u
                    )
                );
                setSuccessMessage(`User ${newLeaderValue === 1 ? 'marked as' : 'unmarked as'} leader`);
                setTimeout(() => setSuccessMessage(null), 1000);
            } else {
                setErrorMessage(response.error?.message || 'Failed to update leader flag');
                setTimeout(() => setErrorMessage(null), 1000);
            }
        } catch (err: any) {
            setErrorMessage('An unexpected error occurred');
            setTimeout(() => setErrorMessage(null), 1000);
        }
    };

    if (loading) {
        return (
            <Layout>
                <div data-theme={isDarkMode ? 'dark' : 'light'}>
                    <Spinner />
                </div>
            </Layout>
        );
    }

    if (error) {
        return (
            <Layout>
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                    <Typography variant="h6" color="error">{error}</Typography>
                </Box>
            </Layout>
        );
    }

    return (
        <Layout>
            <Box sx={{
             
                backgroundColor: theme.palette.background.default,
                borderRadius: '8px',
                margin: { xs: '8px', sm: '16px' },
                minHeight: '100vh'
            }}>
                {/* Header */}
                <Box mb={4}>
                    <Typography
                        variant="h4"
                        component="h1"
                        sx={{
                            color: theme.palette.text.primary,
                            fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                            fontWeight: { xs: 600, sm: 700 },
                            lineHeight: { xs: 1.2, sm: 1.3 },
                            mb: 1
                        }}
                    >
                        All Users
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: theme.palette.text.secondary,
                            fontSize: { xs: '0.875rem', sm: '1rem' }
                        }}
                    >
                        View all users in the assessment system. Total users: {allUsers.length}
                    </Typography>
                </Box>

                {/* Search Bar */}
                <Box sx={{ mb: 3 }}>
                    <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Search by name or email..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search size={20} />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            maxWidth: '500px',
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#fff',
                            }
                        }}
                    />
                    {searchQuery && (
                        <Typography
                            variant="body2"
                            sx={{ mt: 1, color: theme.palette.text.secondary }}
                        >
                            Found {filteredUsers.length} user(s)
                        </Typography>
                    )}
                </Box>

                {/* Users Table */}
                <Card sx={{
                    backgroundColor: isDarkMode
                        ? 'rgba(255, 255, 255, 0.05)'
                        : theme.palette.background.paper,
                    borderRadius: 2,
                    border: isDarkMode
                        ? '1px solid rgba(255, 255, 255, 0.1)'
                        : `1px solid ${theme.palette.divider}`,
                }}>
                    <CardContent sx={{ p: 0 }}>
                        <TableContainer component={Paper} sx={{
                            backgroundColor: 'transparent',
                            boxShadow: 'none',
 
                        }}>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{
                                            backgroundColor: isDarkMode ? '#1A2B4D' : '#E3F2FD',
                                            color: theme.palette.text.primary,
                                            fontWeight: 700,
                                            fontSize: '0.95rem'
                                        }}>
                                            Name
                                        </TableCell>
                                        <TableCell sx={{
                                            backgroundColor: isDarkMode ? '#1A2B4D' : '#E3F2FD',
                                            color: theme.palette.text.primary,
                                            fontWeight: 700,
                                            fontSize: '0.95rem'
                                        }}>
                                            Email
                                        </TableCell>
                                        <TableCell sx={{
                                            backgroundColor: isDarkMode ? '#1A2B4D' : '#E3F2FD',
                                            color: theme.palette.text.primary,
                                            fontWeight: 700,
                                            fontSize: '0.95rem'
                                        }}>
                                            Date of Birth
                                        </TableCell>
                                        <TableCell sx={{
                                            backgroundColor: isDarkMode ? '#1A2B4D' : '#E3F2FD',
                                            color: theme.palette.text.primary,
                                            fontWeight: 700,
                                            fontSize: '0.95rem'
                                        }}>
                                            Role
                                        </TableCell>
                                        <TableCell sx={{
                                            backgroundColor: isDarkMode ? '#1A2B4D' : '#E3F2FD',
                                            color: theme.palette.text.primary,
                                            fontWeight: 700,
                                            fontSize: '0.95rem',
                                            textAlign: 'center'
                                        }}>
                                            Leader
                                        </TableCell>
                                        <TableCell sx={{
                                            backgroundColor: isDarkMode ? '#1A2B4D' : '#E3F2FD',
                                            color: theme.palette.text.primary,
                                            fontWeight: 700,
                                            fontSize: '0.95rem',
                                            textAlign: 'center'
                                        }}>
                                            Actions
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {paginatedUsers.length === 0 ? (
                                        <TableRow>
                                            <TableCell
                                                colSpan={6}
                                                sx={{
                                                    textAlign: 'center',
                                                    padding: '40px',
                                                    color: theme.palette.text.secondary
                                                }}
                                            >
                                                {searchQuery ? 'No users found matching your search' : 'No users found'}
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        paginatedUsers.map((user) => (
                                        <TableRow
                                            key={user.userId}
                                            sx={{
                                                '&:hover': {
                                                    backgroundColor: isDarkMode
                                                        ? 'rgba(255, 255, 255, 0.05)'
                                                        : 'rgba(0, 0, 0, 0.02)',
                                                },
                                                transition: 'background-color 0.2s ease'
                                            }}
                                        >
                                            <TableCell sx={{ color: theme.palette.text.primary }}>
                                                {user.name}
                                            </TableCell>
                                            <TableCell sx={{ color: theme.palette.text.primary }}>
                                                {user.email}
                                            </TableCell>
                                            <TableCell sx={{ color: theme.palette.text.primary }}>
                                                {formatDate(user.dob)}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={user.role}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: user.role === 'Admin'
                                                            ? (isDarkMode ? '#4AE290' : '#4CAF50')
                                                            : (isDarkMode ? '#4A90E2' : '#2196F3'),
                                                        color: isDarkMode ? '#000' : '#fff',
                                                        fontWeight: 600,
                                                        fontSize: '0.8rem'
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ textAlign: 'center' }}>
                                                <Checkbox
                                                    checked={user.is_leader === 1}
                                                    onChange={() => handleLeaderToggle(user)}
                                                    sx={{
                                                        color: isDarkMode ? '#64b5f6' : '#1976d2',
                                                        '&.Mui-checked': {
                                                            color: isDarkMode ? '#4AE290' : '#4CAF50',
                                                        }
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ textAlign: 'center' }}>
                                                <Tooltip title="Edit User">
                                                    <IconButton
                                                        onClick={() => handleEditClick(user)}
                                                        sx={{
                                                            color: isDarkMode ? '#64b5f6' : '#1976d2',
                                                            '&:hover': {
                                                                backgroundColor: isDarkMode ? 'rgba(100, 181, 246, 0.1)' : 'rgba(25, 118, 210, 0.1)'
                                                            }
                                                        }}
                                                    >
                                                        <Edit2 size={18} />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete User">
                                                    <IconButton
                                                        onClick={() => handleDeleteClick(user)}
                                                        sx={{
                                                            color: isDarkMode ? '#ef5350' : '#d32f2f',
                                                            '&:hover': {
                                                                backgroundColor: isDarkMode ? 'rgba(239, 83, 80, 0.1)' : 'rgba(211, 47, 47, 0.1)'
                                                            }
                                                        }}
                                                    >
                                                        <Trash2 size={18} />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    )))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>

                {/* Pagination */}
                {filteredUsers.length > 0 && totalPages > 1 && (
                    <Box
                        sx={{
                            mt: 3,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}
                    >
                        <Stack spacing={2}>
                            <Pagination
                                count={totalPages}
                                page={currentPage}
                                onChange={handlePageChange}
                                color="primary"
                                size="large"
                                showFirstButton
                                showLastButton
                                sx={{
                                    '& .MuiPaginationItem-root': {
                                        color: theme.palette.text.primary,
                                        fontSize: '1rem',
                                        fontWeight: 500,
                                    },
                                    '& .Mui-selected': {
                                        backgroundColor: theme.palette.primary.main,
                                        color: '#fff',
                                        '&:hover': {
                                            backgroundColor: theme.palette.primary.dark,
                                        },
                                    },
                                }}
                            />
                            <Typography
                                variant="body2"
                                sx={{
                                    color: theme.palette.text.secondary,
                                    textAlign: 'center'
                                }}
                            >
                                Showing {((currentPage - 1) * usersPerPage) + 1} - {Math.min(currentPage * usersPerPage, filteredUsers.length)} of {filteredUsers.length} users
                            </Typography>
                        </Stack>
                    </Box>
                )}

                {/* Success/Error Messages */}
                {successMessage && (
                    <div data-theme={isDarkMode ? 'dark' : 'light'}>
                        <div className="alert alert-success">
                            <CheckCircle size={20} />
                            <span>{successMessage}</span>
                        </div>
                    </div>
                )}

                {errorMessage && (
                    <div data-theme={isDarkMode ? 'dark' : 'light'}>
                        <div className="alert alert-error">
                            <AlertCircle size={20} />
                            <span>{errorMessage}</span>
                        </div>
                    </div>
                )}

                {/* Edit User Modal */}
                {editModalOpen && (
                    <div data-theme={isDarkMode ? 'dark' : 'light'}>
                        <div className="modal-overlay" onClick={handleEditClose}>
                            <div className="modal-container" onClick={(e) => e.stopPropagation()}>
                                <div className="modal-header">
                                    <h2 className="modal-title">Edit User</h2>
                                    <button className="modal-close" onClick={handleEditClose}>
                                        <X size={24} />
                                    </button>
                                </div>

                                <div className="modal-body">
                                    <div className="modal-form">
                                        <div className="form-group">
                                            <label className="form-label">Name</label>
                                            <input
                                                type="text"
                                                className="form-input"
                                                value={editFormData.name}
                                                onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                                                required
                                            />
                                        </div>

                                        <div className="form-group">
                                            <label className="form-label">Email</label>
                                            <input
                                                type="email"
                                                className="form-input"
                                                value={editFormData.email}
                                                onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                                                required
                                            />
                                        </div>

                                        <div className="form-group">
                                            <label className="form-label">Date of Birth</label>
                                            <input
                                                type="date"
                                                className="form-input"
                                                value={editFormData.dob}
                                                onChange={(e) => setEditFormData({ ...editFormData, dob: e.target.value })}
                                                required
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="modal-actions">
                                    <button
                                        type="button"
                                        className="modal-btn modal-btn-cancel"
                                        onClick={handleEditClose}
                                        disabled={editLoading}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="modal-btn modal-btn-primary"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            const formElement = e.currentTarget.closest('.modal-container')?.querySelector('.modal-form');
                                            const inputs = formElement?.querySelectorAll('input[required]');
                                            let isValid = true;
                                            inputs?.forEach((input: any) => {
                                                if (!input.value) {
                                                    isValid = false;
                                                    input.reportValidity();
                                                }
                                            });
                                            if (isValid) {
                                                handleEditSubmit(e as any);
                                            }
                                        }}
                                        disabled={editLoading}
                                    >
                                        {editLoading ? (
                                            <>
                                                <div className="btn-spinner" />
                                                Updating...
                                            </>
                                        ) : (
                                            'Update User'
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Delete User Modal */}
                {deleteModalOpen && (
                    <div data-theme={isDarkMode ? 'dark' : 'light'}>
                        <div className="modal-overlay" onClick={handleDeleteClose}>
                            <div className="modal-container" onClick={(e) => e.stopPropagation()}>
                                <div className="modal-header">
                                    <h2 className="modal-title">Delete User</h2>
                                    <button className="modal-close" onClick={handleDeleteClose}>
                                        <X size={24} />
                                    </button>
                                </div>

                                <div className="modal-body">
                                    <div className="delete-confirmation">
                                        <p>Are you sure you want to delete this user? This action cannot be undone.</p>
                                        {deletingUser && (
                                            <div className="delete-user-info">
                                                <p><strong>Name:</strong> {deletingUser.name}</p>
                                                <p><strong>Email:</strong> {deletingUser.email}</p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="modal-actions">
                                    <button
                                        type="button"
                                        className="modal-btn modal-btn-cancel"
                                        onClick={handleDeleteClose}
                                        disabled={deleteLoading}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="modal-btn modal-btn-danger"
                                        onClick={handleDeleteConfirm}
                                        disabled={deleteLoading}
                                    >
                                        {deleteLoading ? (
                                            <>
                                                <div className="btn-spinner" />
                                                Deleting...
                                            </>
                                        ) : (
                                            'Delete User'
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </Box>
        </Layout>
    );
};

export default Users;
